import { lazy, type ReactNode, Suspense } from 'react';

import { useIsStiggMounted } from '@/lib/stigg/hooks/useIsStiggMounted';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';

import type { JobLimitGateContentProps } from './JobLimitGateContent';

interface JobLimitGateProps extends JobLimitGateContentProps {
  /** Shown while billing loads; pages pass a loader so they never render blank. */
  fallback?: ReactNode;
}

// Lazy so the Stigg SDK stays out of the portal chunk; see PortalBillingProvider
// for the matching provider split.
const JobLimitGateContent = lazy(() =>
  import('./JobLimitGateContent').then((module) => ({
    default: module.JobLimitGateContent,
  })),
);

/**
 * Chooses between creating a job and the `reached` UI from Stigg's active-job
 * entitlement. Waits for billing like `FreePlanGate`; when billing is off or
 * Stigg cannot answer, creating stays open and the API remains the real gate.
 */
export function JobLimitGate({ fallback = null, ...props }: JobLimitGateProps) {
  const isStiggMounted = useIsStiggMounted();
  const { isLoading } = useWorkspacePlan();

  if (isLoading) return fallback;
  if (!isStiggMounted) return props.children;

  return (
    <Suspense fallback={fallback}>
      <JobLimitGateContent {...props} />
    </Suspense>
  );
}
